import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';

const UserLanguageContext = createContext();

export function UserLanguageProvider({ children }) {
    const [activeLanguage, setActiveLanguageState] = useState(() =>
        localStorage.getItem('vozoly_target_language') || 'italian'
    );
    const [activeLevel, setActiveLevelState] = useState(() =>
        localStorage.getItem('vozoly_level') || 'A1'
    );
    const [userLanguages, setUserLanguages] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadUserLanguages = useCallback(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        const { data } = await supabase
            .from('user_languages')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true });

        if (data && data.length > 0) {
            setUserLanguages(data);
            const active = data.find(l => l.is_active) || data[0];
            setActiveLanguageState(active.target_language);
            setActiveLevelState(active.level);
            localStorage.setItem('vozoly_target_language', active.target_language);
            localStorage.setItem('vozoly_level', active.level);
        } else {
            // Legacy user with no user_languages row yet — migrate their
            // existing localStorage values into a first row.
            const legacyLang = localStorage.getItem('vozoly_target_language') || 'italian';
            const legacyLevel = localStorage.getItem('vozoly_level') || 'A1';
            const { data: inserted } = await supabase
                .from('user_languages')
                .insert({
                    user_id: user.id,
                    target_language: legacyLang,
                    level: legacyLevel,
                    is_active: true,
                })
                .select()
                .single();
            if (inserted) {
                setUserLanguages([inserted]);
                setActiveLanguageState(inserted.target_language);
                setActiveLevelState(inserted.level);
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadUserLanguages();
    }, [loadUserLanguages]);

    const switchLanguage = async (targetLanguage) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const target = userLanguages.find(l => l.target_language === targetLanguage);
        if (!target) return;

        await supabase.from('user_languages').update({ is_active: false }).eq('user_id', user.id);
        await supabase.from('user_languages').update({ is_active: true }).eq('user_id', user.id).eq('target_language', targetLanguage);

        setActiveLanguageState(targetLanguage);
        setActiveLevelState(target.level);
        localStorage.setItem('vozoly_target_language', targetLanguage);
        localStorage.setItem('vozoly_level', target.level);
        setUserLanguages(prev => prev.map(l => ({ ...l, is_active: l.target_language === targetLanguage })));
    };

    const addLanguage = async (targetLanguage, level) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const existing = userLanguages.find(l => l.target_language === targetLanguage);
        if (existing) {
            await switchLanguage(targetLanguage);
            return existing;
        }

        await supabase.from('user_languages').update({ is_active: false }).eq('user_id', user.id);

        const { data: inserted } = await supabase
            .from('user_languages')
            .insert({
                user_id: user.id,
                target_language: targetLanguage,
                level: level || 'A1',
                is_active: true,
            })
            .select()
            .single();

        if (inserted) {
            setUserLanguages(prev => [...prev, inserted]);
            setActiveLanguageState(inserted.target_language);
            setActiveLevelState(inserted.level);
            localStorage.setItem('vozoly_target_language', inserted.target_language);
            localStorage.setItem('vozoly_level', inserted.level);
        }
        return inserted;
    };

    const deleteLanguage = async (targetLanguage) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { success: false, error: 'Not signed in' };

        const target = userLanguages.find(l => l.target_language === targetLanguage);
        if (!target) return { success: false, error: 'Language not found' };
        if (target.is_active) return { success: false, error: 'Cannot delete the active language' };

        const { error } = await supabase
            .from('user_languages')
            .delete()
            .eq('user_id', user.id)
            .eq('target_language', targetLanguage);

        if (error) return { success: false, error: error.message };

        setUserLanguages(prev => prev.filter(l => l.target_language !== targetLanguage));
        return { success: true };
    };

    const updateActiveLevel = async (newLevel) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        await supabase
            .from('user_languages')
            .update({ level: newLevel })
            .eq('user_id', user.id)
            .eq('target_language', activeLanguage);
        setActiveLevelState(newLevel);
        localStorage.setItem('vozoly_level', newLevel);
        setUserLanguages(prev => prev.map(l => l.target_language === activeLanguage ? { ...l, level: newLevel } : l));
    };

    return (
        <UserLanguageContext.Provider value={{
            activeLanguage,
            activeLevel,
            userLanguages,
            loading,
            switchLanguage,
            addLanguage,
            deleteLanguage,
            updateActiveLevel,
            reloadUserLanguages: loadUserLanguages,
        }}>
            {children}
        </UserLanguageContext.Provider>
    );
}

export function useUserLanguage() {
    const context = useContext(UserLanguageContext);
    if (!context) throw new Error('useUserLanguage must be used within a UserLanguageProvider');
    return context;
}